import { FormElement } from "./FormElement";
import { DOMTemplate } from "../../denki";
import { node } from "../Util";
import { FieldInputElement } from "./FieldInputElement";
import { TemplateManager } from "../template/TemplateManager";

export interface ZipcodeInfo {
  prefecture: string;
  city: string;
  address: string;
}

export type ZipcodeReferenceType = "raw" | "address" | "word" | "city_address" | "city" | "prefecture" | "city_only" | "word_name";

interface ZipcodeReference {
  type: ZipcodeReferenceType;
  element: FieldInputElement;
}

type ZipcodeResolver = (zipcode: string) => Promise<ZipcodeInfo | null> | ZipcodeInfo | null;

let customZipcodeResolver: ZipcodeResolver | undefined;

export const setZipcodeResolver = (resolver: ZipcodeResolver) => {
  customZipcodeResolver = resolver;
};

let zipcodeTableCache: { [zipcode: string]: ZipcodeInfo } | null = null;

async function resolveZipcode(zipcode: string): Promise<ZipcodeInfo | null> {
  const resolver = customZipcodeResolver;
  if (resolver) {
    return await Promise.resolve(resolver(zipcode));
  }
  if (zipcodeTableCache == null) {
    const mod = await import("../../zipcode.json");
    // @ts-ignore
    zipcodeTableCache = mod.default || mod;
  }
  return zipcodeTableCache[zipcode] || null;
}

export class FormZipCodeElement extends FormElement implements FieldInputElement {
  public readonly fieldsElement: HTMLElement;
  public readonly autoFillButton: HTMLButtonElement;
  public readonly first3Input: HTMLInputElement;
  public readonly plus4Input: HTMLInputElement;
  private readonly referers: ZipcodeReference[] = [];
  public validator?: (text: string | number) => Promise<string[]> | string[];

  constructor(name: string, template: DOMTemplate) {
    super(name, template.get("field-element"));
    this.fieldsElement = this.element.querySelector("[data-role=elements]");
    const div = document.createElement("div");
    div.setAttribute("data-template", "input_zipcode");
    this.fieldsElement.appendChild(div);
    const id = name;
    this.virtualInput.setAttribute("data-id", id);
    this.virtualInput.setAttribute("name", id);
    this.first3Input = node('input', {
      type: 'text',
      name: "first_3",
      placeholder: "例：000",
      minlength: "3",
      maxlength: "3",
      "data-id": `${id}-first_3`,
      "data-label": "first_3",
      id: `form_element-${id}-first_3`
    });

    this.plus4Input = node("input", {
      type: "text",
      name: "plus_4",
      placeholder: "例：0000",
      minlength: "4",
      maxlength: "4",
      "data-id": `${id}-plus_4`,
      "data-label": "plus_4",
      id: `form_element-${id}-plus_4`
    });

    this.autoFillButton = node("button", {
      class: "autoInputButton"
    }, "住所を自動入力");

    div.appendChild(node("label", {}, "〒"));
    div.appendChild(this.first3Input);
    div.appendChild(node("label", {}, "-"));
    div.appendChild(this.plus4Input);
    div.appendChild(this.autoFillButton);

    this.virtualInput.addEventListener("change", () => {
      this.decodeValue();
    });

    let isFirst3InputEditing = false;
    this.first3Input.addEventListener("compositionstart", e => {
      isFirst3InputEditing = true;
    });
    this.first3Input.addEventListener("compositionend", e => {
      isFirst3InputEditing = false;
      this.encodeValue();
    });
    let isPlus4InputEditing = false;
    this.plus4Input.addEventListener("compositionstart", e => {
      isPlus4InputEditing = true;
    });
    this.plus4Input.addEventListener("compositionend", e => {
      isPlus4InputEditing = false;
      this.encodeValue();
    });
    ["paste", "cut", "change"].forEach(event => {
      this.first3Input.addEventListener(event, e => {
        if (!isFirst3InputEditing) {
          this.encodeValue();
        }
      });
      this.plus4Input.addEventListener(event, e => {
        if (!isPlus4InputEditing) {
          this.encodeValue();
        }
      });
    });
    this.autoFillButton.addEventListener("click", async () => {
      const zipcode = this.value.replace("-", "");
      try {
        const zipcodeInfo = await resolveZipcode(zipcode);
        if (zipcodeInfo) {
          this.referers.forEach(reference => this.refer(zipcodeInfo, reference));
        } else {
          alert("住所が見つかりませんでした");
        }
      } catch (e) {
        alert("住所検索でエラーが発生しました");
        // eslint-disable-next-line no-console
        console.error(e);
      }
    });
  }

  private refer(zipcodeInfo: ZipcodeInfo, reference: ZipcodeReference) {
    const target = reference.element;
    const value = (() => {
      const city = zipcodeInfo.city;
      const address = zipcodeInfo.address;
      if (reference.type === "raw") return zipcodeInfo.prefecture + city + address;
      if (reference.type === "city_address") return city + address;
      if (reference.type === "city") return zipcodeInfo.prefecture + city;
      if (reference.type === "address") return zipcodeInfo.address;
      if (reference.type === "word") {
        // 一旦パワープレイ
        // 市を名称に含む政令指定都市は現状ないのでセーフ
        const index = city.indexOf("市");
        return city.substring(index + 1);
      }
      if (reference.type === "word_name") {
        const index = city.indexOf("市");
        return city.substring(index + 1, city.length - 1);
      }
      if (reference.type === "city_only") {
        return zipcodeInfo.city;
      }
      if (reference.type === "prefecture") {
        return zipcodeInfo.prefecture;
      }
    })();
    target.value = value;
  }

  private encodeValue() {
    let first3 = TemplateManager.Util.convertToASCIIDigit(this.first3Input.value);
    const plus4 = TemplateManager.Util.convertToASCIIDigit(this.plus4Input.value);
    setTimeout(() => {
      // first3が埋まり切ってないと-が表示されてしまうので空白を詰めておく
      first3 = first3 + " ".repeat(3 - first3.length);
      if (first3 === "   " && !plus4) {
        this.value = "";
      } else {
        this.value = `${first3}-${plus4}`;
      }
    }, 0);
  }

  private decodeValue() {
    try {
      const value = JSON.parse(this.virtualInput.value);
      if (value) {
        const token = value.split("-");
        this.first3Input.value = (token[0] || "").trim();
        this.plus4Input.value = (token[1] || "").trim();
      } else {
        this.first3Input.value = "";
        this.plus4Input.value = "";
      }
    } catch (e) {
      this.first3Input.value = "";
      this.plus4Input.value = "";
    }
    this.updateValidation();
  }

  get validationErrors(): Promise<string[]> {
    this.first3Input.classList.remove("error");
    this.plus4Input.classList.remove("error");

    let errors = [];
    const errorFirst = TemplateManager.Util.createValidatorDigit("上", 3)(this.first3Input.value);
    if (errorFirst.length > 0) {
      errors = errors.concat(errorFirst);
      this.first3Input.classList.add("error");
    }
    const errorPlus = TemplateManager.Util.createValidatorDigit("下", 4)(this.plus4Input.value);
    if (errorPlus.length > 0) {
      errors = errors.concat(errorPlus);
      this.plus4Input.classList.add("error");
    }
    const validator = this.validator;
    if (!validator) return Promise.resolve(errors);
    if (errors.length === 0) {
      return Promise.resolve(validator(this.value));
    }
    return Promise.resolve(errors);
  }

  public focus() {
    this.first3Input.focus();
  }

  public addReferer(element: FieldInputElement, type: ZipcodeReferenceType) {
    this.referers.push({
      type, element
    });
  }

  public get mapKeys(): string[] {
    return ["first_3", "plus_4"];
  }

  public subInput(name: string) {
    if (name === "first_3") return this.first3Input;
    if (name === "plus_4") return this.plus4Input;
    return null;
  }
}
